import { Button, useToast } from '@uroute/ui';
import { useEffect, useState } from 'react';
import { haptic } from '../lib/haptics';
import { useOnline } from '../lib/offline';
import { discardItem, retryAll, retryItem, useQueue, type QueueItem } from '../lib/queue';
import { Sheet } from './Sheet';
import { AlertTriangle, Check, RefreshCw, Trash, WifiOff } from './icons';

export interface SyncSheetProps {
  open: boolean;
  onClose: () => void;
}

function itemRow(
  item: QueueItem,
  expanded: string | null,
  setExpanded: (id: string | null) => void,
  busy: boolean,
  online: boolean,
  handle: (action: () => Promise<void>, message: string) => Promise<void>,
) {
  const isConflicted = item.state === 'failed' && item.conflict === true;
  const statusLabel =
    item.state === 'done'
      ? 'Synced'
      : item.state === 'failed'
        ? isConflicted
          ? 'Needs review'
          : 'Failed'
        : 'Waiting';

  return (
    <li
      key={item.id}
      className={`sync-item sync-item--${item.state}${isConflicted ? ' sync-item--conflict' : ''}`}
      data-testid="sync-item"
      data-item-kind={item.kind}
      data-item-state={item.state}
      data-item-conflict={isConflicted ? 'true' : 'false'}
    >
      <button
        type="button"
        className="sync-item__main"
        onClick={() => setExpanded(expanded === item.id ? null : item.id)}
        aria-expanded={expanded === item.id}
      >
        <span className="sync-item__icon" aria-hidden="true">
          {item.state === 'done' ? (
            <Check size={14} />
          ) : isConflicted ? (
            <AlertTriangle size={14} />
          ) : (
            <span className={`sync-item__dot sync-item__dot--${item.state}`} />
          )}
        </span>
        <span className="sync-item__body">
          <span className="sync-item__label">{item.label}</span>
          <span className="sync-item__meta u-data">
            {item.kind} · {statusLabel}
            {item.attempts > 0 ? ` · attempt ${item.attempts}` : ''}
            {item.error ? ` · ${item.error}` : ''}
          </span>
        </span>
      </button>
      {expanded === item.id ? (
        <div className="sync-item__detail">
          <pre className="sync-item__json">{JSON.stringify(item.json ?? item.fields ?? {}, null, 2)}</pre>
          <div className="sync-item__actions">
            {item.state !== 'done' ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handle(() => retryItem(item.id), 'Retried')}
                disabled={busy || !online}
                data-testid="sync-retry"
              >
                <RefreshCw size={14} />
                Retry
              </Button>
            ) : null}
            {item.state !== 'done' ? (
              <Button
                variant="danger"
                size="sm"
                onClick={() => void handle(() => discardItem(item.id), 'Discarded')}
                disabled={busy}
                data-testid="sync-discard"
              >
                <Trash size={14} />
                Discard
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Offline queue manager: lists pending / failed / conflict items with
 * retry, discard, and a manual "retry all" action.
 */
export function SyncSheet({ open, onClose }: SyncSheetProps) {
  const online = useOnline();
  const toast = useToast();
  const { items, failed, syncing } = useQueue();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setExpanded(null);
  }, [open]);

  const handle = async (action: () => Promise<void>, message: string) => {
    setBusy(true);
    try {
      await action();
      toast.show({ title: message, tone: 'success' });
      haptic('light');
    } catch {
      toast.show({ title: 'Action failed', tone: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Sync queue"
      testId="sync-sheet"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => void handle(() => retryAll(), 'Retrying all items')}
            loading={busy}
            disabled={!online || failed === 0}
            data-testid="sync-retry-all"
            className="sheet__primary"
          >
            <RefreshCw size={16} />
            Retry all
          </Button>
        </>
      }
    >
      {!online ? (
        <p className="notice notice--warning">
          <WifiOff size={16} />
          <span>You&rsquo;re offline — items will sync when you reconnect.</span>
        </p>
      ) : null}
      {syncing ? (
        <p className="notice notice--info" role="status">
          <RefreshCw size={16} className="spin" />
          <span>Syncing in progress…</span>
        </p>
      ) : null}

      {items.length === 0 ? (
        <p className="empty-note" data-testid="sync-empty">
          Nothing to sync — you&rsquo;re all caught up.
        </p>
      ) : (
        <ul className="sync-list">
          {items.map((item) => itemRow(item, expanded, setExpanded, busy, online, handle))}
        </ul>
      )}
    </Sheet>
  );
}
